import React from "react";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const UOM_OPTIONS = [
    "kg", "g", "mg", "ton", "m", "mm", "cm", "m²", "m³",
    "L", "mL", "pcs", "set", "sheet", "ft", "inch",
];

/**
 * Conversion table — each unit's "base value" in its family's canonical unit.
 * For example, 1 g = 0.001 kg (weight family base = kg).
 * Same family → price can be auto-converted on UOM change.
 */
export const UOM_BASE = {
    kg: { family: "weight", base: 1 },
    g: { family: "weight", base: 0.001 },
    mg: { family: "weight", base: 1e-6 },
    ton: { family: "weight", base: 1000 },
    m: { family: "length", base: 1 },
    mm: { family: "length", base: 0.001 },
    cm: { family: "length", base: 0.01 },
    ft: { family: "length", base: 0.3048 },
    inch: { family: "length", base: 0.0254 },
    "m²": { family: "area", base: 1 },
    "m³": { family: "volume", base: 1 },
    L: { family: "volume", base: 0.001 },
    mL: { family: "volume", base: 1e-6 },
    pcs: { family: "count", base: 1 },
    set: { family: "count", base: 1 },
    sheet: { family: "count", base: 1 },
};

/**
 * Returns a multiplier to convert a price `₹/from` to `₹/to`.
 * If the units are in different families (or unknown), returns 1 (no conversion).
 *
 *   price_new = price_old * uomPriceFactor(from, to)
 *
 * e.g., uomPriceFactor("kg", "g") = 0.001  ⇒ 80 ₹/kg becomes 0.08 ₹/g.
 */
export function uomPriceFactor(from, to) {
    if (!from || !to || from === to) return 1;
    const a = UOM_BASE[from], b = UOM_BASE[to];
    if (!a || !b) return 1;
    if (a.family !== b.family) return 1;
    return b.base / a.base;
}

/**
 * Converts a *quantity value* (a weight, length, count etc.) from one unit
 * to another within the same family.
 *
 *   value_new = uomConvertValue(value_old, from, to)
 *
 * e.g., uomConvertValue(37, "g", "kg") = 0.037  ⇒ 37 g becomes 0.037 kg.
 * Returns the value unchanged if from/to are the same family but the
 * conversion lookup fails (graceful degradation).
 */
export function uomConvertValue(value, from, to) {
    const v = Number(value) || 0;
    if (!from || !to || from === to) return v;
    const a = UOM_BASE[from], b = UOM_BASE[to];
    if (!a || !b) return v;
    if (a.family !== b.family) return v;
    return v * (a.base / b.base);
}

/**
 * UOM dropdown with an "Other…" free-text fallback.
 *
 * Props:
 *  - unit: current standard or custom unit string
 *  - unitOther: truthy marker that "Other…" mode is active even when unit is ""
 *  - onChange({ unit, unit_other })  parent persists both
 *  - testIdPrefix (optional)         data-testid roots
 *  - triggerClassName (optional)
 */
export default function UomSelect({ unit = "", unitOther = "", onChange, testIdPrefix = "uom", triggerClassName = "rounded-none h-8" }) {
    const isStdUom = UOM_OPTIONS.includes(unit);
    const isOtherMode = !isStdUom && (unit !== "" || unitOther !== "");
    const selectValue = isOtherMode ? "__other__" : (isStdUom ? unit : "");

    const handleChange = (v) => {
        if (v === "__other__") {
            // Enter Other mode: carry non-standard current value, otherwise mark via single-space unit_other
            const carry = isStdUom ? "" : unit;
            onChange({ unit: carry, unit_other: carry || " " });
        } else {
            onChange({ unit: v, unit_other: "" });
        }
    };

    return (
        <>
            <Select value={selectValue} onValueChange={handleChange}>
                <SelectTrigger className={triggerClassName} data-testid={`${testIdPrefix}-trigger`}>
                    <SelectValue placeholder="UOM" />
                </SelectTrigger>
                <SelectContent>
                    {UOM_OPTIONS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    <SelectItem value="__other__">Other…</SelectItem>
                </SelectContent>
            </Select>
            {isOtherMode && (
                <Input
                    value={unit}
                    onChange={(e) => onChange({ unit: e.target.value, unit_other: e.target.value || " " })}
                    placeholder="custom UOM"
                    className="h-7 rounded-none font-mono mt-1 text-xs"
                    data-testid={`${testIdPrefix}-other`}
                    autoFocus
                />
            )}
        </>
    );
}
